import {CSSProperties} from 'react';

/**
 * Defines how a component prop maps to a CSS style property.
 * Uses `unknown` for flexibility - transform functions handle type narrowing.
 */
export type StyleMapping = {
  /** The prop name to read from */
  prop: string;
  /** The CSS property to write to */
  styleProp: keyof CSSProperties;
  /** Optional transform function to convert prop value to CSS value */
  transform?: (value: unknown) => string | number | undefined;
};

/**
 * Builds a CSSProperties object from component props using a declarative schema.
 *
 * This replaces repetitive if-statement patterns like:
 * ```
 * if (props.width !== undefined) { style.width = toSizeValue(props.width); }
 * if (props.height !== undefined) { style.height = toSizeValue(props.height); }
 * ```
 *
 * With a declarative mapping:
 * ```
 * buildStylesFromSchema(props, [
 *   { prop: 'width', styleProp: 'width', transform: toSizeValue },
 *   { prop: 'height', styleProp: 'height', transform: toSizeValue },
 * ])
 * ```
 *
 * @param props - Component props object
 * @param mappings - Array of style mappings defining prop-to-style relationships
 * @returns CSSProperties object with all defined styles
 */
export const buildStylesFromSchema = (
  props: Record<string, unknown>,
  mappings: ReadonlyArray<StyleMapping>
): CSSProperties => {
  const style: CSSProperties = {};

  for (const mapping of mappings) {
    const value = props[mapping.prop];

    if (value === undefined) {
      continue;
    }

    const cssValue = mapping.transform ? mapping.transform(value) : value;

    // Skip if transform returned undefined (e.g., responsive value without base)
    if (cssValue === undefined) {
      continue;
    }

    (style as Record<string, unknown>)[mapping.styleProp as string] = cssValue;
  }

  return style;
};
